import { downloadCSVData } from '@/shared/lib/util'
import { Switch } from 'antd'
import { DealBean, ListResponseDealBean } from 'common/api/api'
import { dealApi } from 'common/api/entitiesApi.tsx'
import { processError } from 'common/api/fetch.ts'
import { ActiveStatusSelector } from 'common/components/dictionary/ActiveStatusSelector.tsx'
import { GoToReportButton } from 'common/components/GoToReportButton.tsx'
import { ListCard } from 'common/components/ListCard.tsx'
import { SearchInput } from 'common/components/SearchInput.tsx'
import { ListFilters, useAppContext } from 'common/context/AppContext.tsx'
import { CommonRoutes } from 'common/context/commonRoutes'
import { initListFilters, useListFilters } from 'common/util/useListFilters.ts'
import { apiCall, showError } from 'common/util/Util.tsx'
import * as React from 'react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import {COLUMN_ID_WIDTH, COLUMN_NAME_WIDTH} from 'common/util/constants.ts'

interface DealListProps {
  dealReportSelected: (openKeys: string, selectedKey: string) => void
}

const DealList: React.FC<DealListProps> = ({ dealReportSelected }) => {
  const [listData, setListData] = useState<ListResponseDealBean>()
  const appContext = useAppContext()
  const [loading, setLoading] = useState<boolean>(false)

  const [filters, setFilters] = useState<ListFilters>(initListFilters({ sort: { name: 'id', direction: 'desc' } }))
  const { setFilter } = useListFilters(setFilters)

  const loadData = () => {
    apiCall(
      setLoading,
      appContext,
      () =>
        dealApi.dealPage(filters.sort?.name, filters.sort?.direction, filters.name, filters.active, {
          query: { size: filters.pageSize, page: filters.page },
        }),
      (bean: ListResponseDealBean) => setListData(bean),
    )
  }

  const handleCsv = () => {
    dealApi
      .getDealCsv()
      .then((response: Response) => {
        return Promise.resolve(
          response
            .blob()
            .then((blob) => {
              return Promise.resolve(downloadCSVData('DealList.csv', blob))
            })
            .catch((err) => {
              return processError(err)
            }),
        )
      })
      .catch((err) => {
        return onError(err)
      })
  }

  const onError = (e) => {
    setLoading(false)
    showError(appContext, e)
  }

  const toggleActive = (record: DealBean, checked: boolean) => {
    if (record.id) {
      const id = record.id
      apiCall(
        setLoading,
        appContext,
        () => (checked ? dealApi.dealActivate(id) : dealApi.dealDeactivate(id)),
        () => {
          record.active = checked
          setListData(listData)
        },
      )
    }
  }

  return (
    <ListCard
      loading={loading}
      title="Deals"
      listRouteKey={CommonRoutes.DEAL}
      filters={filters}
      setFilters={setFilters}
      loadData={loadData}
      listData={listData}
      createName="Deal"
      createRoute={CommonRoutes.DEAL_CREATE}
      additionalButtons={[
        {
          name: 'Export Raw Data',
          onClick: handleCsv,
        },
      ]}
      columns={[
        {
          title: 'ID',
          dataIndex: 'id',
          width: COLUMN_ID_WIDTH,
          fixed: 'left',
        },
        {
          title: 'Name',
          dataIndex: 'name',
          className: 'list-table-name',
          sorter: true,
          ellipsis: true,
          render: (text, record) => (
            <Link style={{ color: '#5888CF', minWidth: 100 }} to={`${CommonRoutes.DEAL}/${record.id}/edit`}>
              {text}
            </Link>
          ),
          width: COLUMN_NAME_WIDTH,
          fixed: 'left',
        },
        {
          title: 'Deal ID',
          dataIndex: 'dealId',
          width: 120,
        },
        {
          title: "Last Modified",
          dataIndex: "modified",
          sorter: true,
          width: 100,
          render: (date) => date && dayjs(date).format('DD MMM YYYY')
        },
        {
          title: 'Active',
          dataIndex: 'active',
          sorter: true,
          render: (val, record) => (
            <Switch size="small" checked={val} onChange={(checked) => record.id && toggleActive(record, checked)} />
          ),
          width: 70,
        },
        {
          title: 'Actions',
          render: (_text: string, record: DealBean) => (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <GoToReportButton title="Report Builder" filterName="DealId" id={record.id} />
            </div>
          ),
          width: 120,
        },
      ]}
    >
      <SearchInput value={filters.name} onChange={(value) => setFilter('name', value)} />
      <ActiveStatusSelector
        placeholder="Filter by Active"
        style={{ minWidth: '100px' }}
        value={filters.active}
        onChange={(value) => setFilter('active', value)}
      />
    </ListCard>
  )
}

export default DealList
